import { useCallback, useEffect, useRef, useState } from "react";

import { API_URL } from "../../../env";
import {
  MDBIcon,
} from "../../mdbCompat";

const DEMO_IMAGE = "https://randomuser.me/api/portraits/men/75.jpg"; // Demo user image

const ImageUpload = () => {
    const [image, setImage] = useState(DEMO_IMAGE);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const fileInputRef = useRef(null);
    const objectUrlRef = useRef("");

    const userId = (sessionStorage.getItem("userId") || "").replaceAll('"', "").trim();
    const token = sessionStorage.getItem("token") || "";

    const resolveImageUrl = useCallback((value) => {
        if (!value) {
            return DEMO_IMAGE;
        }

        if (/^https?:\/\//i.test(value)) {
            return value;
        }

        return `${API_URL}${value}`;
    }, []);

    const updateImage = useCallback((value, isObjectUrl = false) => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = "";
        }

        if (isObjectUrl) {
            objectUrlRef.current = value;
            setImage(value);
            return;
        }

        setImage(resolveImageUrl(value));
    }, [resolveImageUrl]);

    useEffect(() => {
        const loadCurrentProfileImage = async () => {
            if (!userId || !token) {
                return;
            }

            try {
                const response = await fetch(`${API_URL}/profile/viewProfileImage/${encodeURIComponent(userId)}`, {
                    headers: {
                        env: "local",
                        token
                    }
                });

                if (response.ok) {
                    const contentType = (response.headers.get("content-type") || "").toLowerCase();

                    if (contentType.includes("application/json")) {
                        const payload = await response.json().catch(() => null);
                        const profileImage =
                            payload?.responseData?.profile ||
                            payload?.responseData?.imageUrl ||
                            payload?.data?.profile ||
                            payload?.data?.imageUrl ||
                            payload?.profile ||
                            payload?.imageUrl ||
                            "";

                        if (profileImage) {
                            updateImage(profileImage);
                            return;
                        }
                    } else if (contentType.startsWith("image/")) {
                        const imageBlob = await response.blob();
                        if (imageBlob.size > 0) {
                            const imageObjectUrl = URL.createObjectURL(imageBlob);
                            updateImage(imageObjectUrl, true);
                            return;
                        }
                    } else {
                        const textBody = await response.text().catch(() => "");
                        if (textBody && textBody.trim()) {
                            updateImage(textBody.trim());
                            return;
                        }
                    }
                }

                // Fallback: keep compatibility with existing profile API.
                const profileResponse = await fetch(`${API_URL}/profile/getProfileBasedOnUserId/${encodeURIComponent(userId)}`, {
                    headers: {
                        env: "local",
                        token
                    }
                });

                if (!profileResponse.ok) {
                    return;
                }

                const payload = await profileResponse.json().catch(() => null);
                const profile = payload?.responseData ?? payload?.data ?? payload;
                const profileImage = profile?.profile;

                if (profileImage) {
                    updateImage(profileImage);
                }
            } catch (error) {
                console.error("Failed to load profile image", error);
            }
        };

        loadCurrentProfileImage();
    }, [token, updateImage, userId]);

    useEffect(() => () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = "";
        }
    }, []);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImage(reader.result);
            };
            reader.readAsDataURL(file);

            if (!userId) {
                setUploadError("User id not found. Please login again.");
                return;
            }

            if (!token) {
                setUploadError("Session token not found. Please login again.");
                return;
            }

            setUploading(true);
            setUploadError("");

            try {
                const formData = new FormData();
                formData.append("file", file);

                const response = await fetch(`${API_URL}/profile/uploadProfileImage/${encodeURIComponent(userId)}`, {
                    method: "POST",
                    headers: {
                        token
                    },
                    body: formData
                });

                const payload = await response.json().catch(() => null);
                if (!response.ok) {
                    throw new Error(payload?.message || payload?.errorData?.message || "Unable to upload profile image.");
                }

                const uploadedImage = payload?.responseData?.profile;
                if (uploadedImage) {
                    updateImage(uploadedImage);
                }
            } catch (error) {
                setUploadError(error?.message || "Unable to upload profile image.");
            } finally {
                setUploading(false);
            }
        }
    };

    const handleEditClick = () => {
        fileInputRef.current.click();
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative", width: 120, height: 120 }}>
                <img
                    src={image}
                    alt="User"
                    style={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #eee",
                    }}
                />
                <button
                    type="button"
                    onClick={handleEditClick}
                    disabled={uploading}
                    style={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        background: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        cursor: uploading ? "not-allowed" : "pointer",
                        opacity: uploading ? 0.7 : 1,
                    }}
                >
                    <MDBIcon icon="edit" color="secondary" />
                </button>
                <input
                    type="file"
                    id="formFile"
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                />
            </div>
            {uploading && <span style={{ fontSize: 12, color: "#5e6b6c", fontWeight: 700 }}>Uploading...</span>}
            {uploadError && <span style={{ fontSize: 12, color: "#b42318", fontWeight: 700, textAlign: "center" }}>{uploadError}</span>}
        </div>
    );
};

export default ImageUpload;